'use client'

import { Star } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

interface RatingProps {
  /**
   * The maximum rating value
   * @default 5
   */
  maxRating?: number
  /**
   * The current rating value
   * @default 0
   */
  value?: number
  /**
   * Whether the rating is read-only
   * @default false
   */
  readOnly?: boolean
  /**
   * Whether to allow half-star ratings
   * @default true
   */
  allowHalf?: boolean
  /**
   * The size of the rating stars
   * @default "md"
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  /**
   * The color of the filled stars
   * @default undefined (uses primary color)
   */
  color?: string
  /**
   * Optional label for the rating
   */
  label?: string
  /**
   * Callback when rating changes
   */
  onChange?: (value: number) => void
  /**
   * Additional CSS class names
   */
  className?: string
  /**
   * Whether to show the rating text
   * @default true
   */
  showText?: boolean
}

export function Rating({
  maxRating = 5,
  value = 0,
  readOnly = false,
  allowHalf = true,
  showText = true,
  size = 'md',
  color,
  label,
  onChange,
  className,
  ...props
}: RatingProps) {
  const [rating, setRating] = React.useState(value)
  const [hoverRating, setHoverRating] = React.useState(0)

  React.useEffect(() => {
    setRating(value)
  }, [value])

  const handleMouseMove = (
    e: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (readOnly || !allowHalf) return

    // Get the bounding rectangle of the target element
    const rect = e.currentTarget.getBoundingClientRect()

    // Calculate the mouse position relative to the element
    const x = e.clientX - rect.left

    // Determine if the mouse is on the left half or right half
    const isLeftHalf = x < rect.width / 2

    // Set the hover rating based on the mouse position
    const newHoverRating = isLeftHalf ? index + 0.5 : index + 1
    setHoverRating(newHoverRating)
  }

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (readOnly) return

    let newRating: number

    if (allowHalf) {
      // Get the bounding rectangle of the target element
      const rect = e.currentTarget.getBoundingClientRect()

      // Calculate the mouse position relative to the element
      const x = e.clientX - rect.left

      // Determine if the mouse is on the left half or right half
      const isLeftHalf = x < rect.width / 2

      // Set the rating based on the mouse position
      newRating = isLeftHalf ? index + 0.5 : index + 1

      // If clicking on the same value, decrement by 0.5 or set to 0
      if (newRating === rating) {
        newRating = isLeftHalf ? Math.max(0, index) : index + 0.5
      }
    } else {
      // For whole stars only
      newRating = index + 1 === rating ? index : index + 1
    }

    setRating(newRating)
    onChange?.(newRating)
  }

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-10 h-10',
    '2xl': 'w-12 h-12',
  }

  const starSize = sizeClasses[size]

  return (
    <div
      className={cn('flex flex-col gap-1.5', className)}
      role='group'
      aria-label={label || 'Rating'}
      {...props}
    >
      {label && <span className='text-sm font-medium'>{label}</span>}
      <div className='flex items-center'>
        {[...Array(maxRating)].map((_, index) => {
          const starValue = index + 1
          const fillPercentage = Math.min(
            Math.max((rating - index) * 100, 0),
            100
          )
          const hoverFillPercentage = Math.min(
            Math.max((hoverRating - index) * 100, 0),
            100
          )
          const activeFillPercentage = readOnly
            ? fillPercentage
            : hoverRating > 0
              ? hoverFillPercentage
              : fillPercentage

          return (
            <button
              key={index}
              type='button'
              className={cn(
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                readOnly ? 'cursor-default' : 'cursor-pointer',
                'relative transition-transform hover:scale-110'
              )}
              onMouseMove={(e) => !readOnly && handleMouseMove(e, index)}
              onMouseEnter={() =>
                !readOnly && !allowHalf && setHoverRating(starValue)
              }
              onMouseLeave={() => !readOnly && setHoverRating(0)}
              onClick={(e) => handleClick(e, index)}
              disabled={readOnly}
              aria-label={`${allowHalf ? index + 0.5 : starValue} star${starValue !== 1 ? 's' : ''}`}
              aria-valuemin={0}
              aria-valuemax={maxRating}
              aria-valuenow={rating}
              role='slider'
            >
              {/* Background star (empty) */}
              <Star
                className={cn(
                  starSize,
                  'absolute fill-neutral-200 text-yellow-100/50'
                )}
              />

              {/* Foreground star (filled) with clip path */}
              <div
                className='absolute left-0 top-0 overflow-hidden'
                style={{ width: `${activeFillPercentage}%` }}
              >
                <Star
                  className={cn(
                    starSize,
                    'fill-current',
                    color ? color : 'text-yellow-400'
                  )}
                />
              </div>

              {/* Transparent overlay for better click handling */}
              <Star
                className={cn(starSize, 'fill-transparent text-transparent')}
              />
            </button>
          )
        })}
        {showText && !readOnly && (
          <span className='ml-2 text-sm text-muted-foreground'>
            {rating > 0 ? `${rating}/${maxRating}` : 'Chưa đánh giá'}
          </span>
        )}
      </div>
    </div>
  )
}
